import React from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import {
  StatesButtonStates as BUTTON_STATES,
  StatesButton,
  Text,
  TextTypography as TYPOGRAPHY,
  Dialog as DialogComponent,
  Button,
  ButtonPriority,
  HoverStyles,
  ButtonSize,
} from 'wix-ui-tpa/cssVars';
import { DataHooks } from './constants';
import { classes, st } from './Dialog.st.css';
import {
  DialogState,
  DialogType,
  DialogViewModel,
  GeneralDialogViewModel,
} from '../../../components/DailyAgenda/viewModel/dialogViewModel/dialogViewModel';
import { Spinner } from '../Spinner/Spinner';
import { useDailyAgendaActions } from '../../../components/DailyAgenda/hooks/useDailyAgendaActions';
import { WidgetComponents, WidgetElements } from '../../../utils/bi/consts';

export type DialogProps = {
  viewModel: DialogViewModel;
};
const mapDialogTypeToWidgetComponent = (
  dialogType: DialogType,
): WidgetComponents => {
  switch (dialogType) {
    case DialogType.PremiumViewer:
      return WidgetComponents.PREMIUM_VIEWER_DIALOG;
    case DialogType.JoinWaitlist:
      return WidgetComponents.JOIN_WAITLIST_DIALOG;
    default:
      return WidgetComponents.DIALOG;
  }
};

const Dialog: React.FC<DialogProps> = ({ viewModel }) => {
  const { onDialogClose, onElementClicked } = useDailyAgendaActions();
  const { isMobile } = useEnvironment();
  const { isOpen, data, state } = viewModel;
  const { contentText, titleText, confirmButtonUrlLink, closeButtonText } =
    data;
  const isLoading = state === DialogState.LOADING;
  const dialogType = viewModel.type;

  const handleDialogClose = () => {
    onDialogClose();
    onElementClicked(
      mapDialogTypeToWidgetComponent(dialogType),
      WidgetElements.DIALOG_CLOSE_BUTTON,
    );
  };
  return (
    <DialogComponent
      data-hook={DataHooks.Root}
      isOpen={isOpen}
      onClose={handleDialogClose}
      className={st(classes.root, {
        isMobile,
        isLoading,
      })}
      notFullscreenOnMobile
    >
      {titleText && (
        <Text
          className={classes.title}
          data-hook={DataHooks.Title}
          typography={TYPOGRAPHY.largeTitle}
        >
          {titleText}
        </Text>
      )}
      <Text
        className={classes.content}
        data-hook={DataHooks.Content}
        typography={TYPOGRAPHY.listText}
      >
        {contentText}
      </Text>
      <div className={classes.buttonsContainer}>
        {confirmButtonUrlLink ? (
          <a
            href={confirmButtonUrlLink}
            target="_blank"
            data-hook={DataHooks.Link}
            rel="noreferrer"
          >
            <ConfirmButton viewModel={viewModel} />
          </a>
        ) : (
          <ConfirmButton viewModel={viewModel} />
        )}
        {closeButtonText ? <CloseButton text={closeButtonText} /> : null}
      </div>
    </DialogComponent>
  );
};

const ConfirmButton = ({
  viewModel,
}: {
  viewModel: GeneralDialogViewModel;
}) => {
  const { state, data } = viewModel;
  const { onDialogConfirm, onElementClicked } = useDailyAgendaActions();
  const buttonState: BUTTON_STATES =
    state === DialogState.IDLE ? BUTTON_STATES.IDLE : BUTTON_STATES.IN_PROGRESS;
  const dialogType = viewModel.type;
  return (
    <StatesButton
      data-hook={DataHooks.ConfirmButton}
      className={classes.button}
      onClick={() => {
        onDialogConfirm();
        onElementClicked(
          mapDialogTypeToWidgetComponent(dialogType),
          WidgetElements.DIALOG_CONFIRM_BUTTON,
        );
      }}
      state={buttonState}
      size={ButtonSize.small}
      idleContent={
        <Text className={classes.buttonText} typography={TYPOGRAPHY.listText}>
          {data.confirmButtonText}
        </Text>
      }
      inProgressContent={<Spinner />}
    />
  );
};

const CloseButton = ({ text }: { text: string }) => {
  const { onDialogClose } = useDailyAgendaActions();
  const { isMobile } = useEnvironment();

  return (
    <Button
      upgrade
      data-hook={DataHooks.CloseButton}
      onClick={() => onDialogClose()}
      priority={ButtonPriority.basicSecondary}
      size={ButtonSize.small}
      className={classes.closeButton}
      fullWidth={isMobile}
      hoverStyle={HoverStyles.underline}
    >
      {text}
    </Button>
  );
};

export default Dialog;
